/**
 * Event utilities for B2M Tracker
 * Handles event parameter extraction and formatting
 */
export default class EventUtils {
  /**
   * Extract event parameters from an event object
   * Converts to array of {key, value} objects for the API
   * @param {Object} event - The event object
   * @returns {Array} - Array of parameter objects
   */
  extractEventParams(event) {
    const params = [];
    
    // Skip platform and internal properties
    const skipKeys = ['platform', 'event', '_userId', '_timestamp'];
    
    // Process data property if it exists
    if (event.data && typeof event.data === 'object') {
      for (const key in event.data) {
        params.push(this.formatParam(key, event.data[key]));
      }
    }
    
    // Process all other properties
    for (const key in event) {
      if (!skipKeys.includes(key) && key !== 'data') {
        params.push(this.formatParam(key, event[key]));
      }
    }
    
    return params;
  }
  
  /**
   * Type-aware value coercion.
   *
   * Rules:
   *  - null / undefined  → null  (caller should skip the property)
   *  - boolean           → boolean  (never stringify)
   *  - number            → number   (never stringify)
   *  - Date instance     → ISO 8601 string
   *  - ISO-like string   → normalized ISO 8601 string
   *  - numeric-looking string (e.g. "149.90") → kept as string (no coercion)
   *  - Array             → array with each element recursively coerced;
   *                        null/undefined elements are removed
   *  - plain Object      → JSON string
   *  - other string      → unchanged
   *
   * @param {any} val
   * @returns {boolean|number|string|Array|null}
   */
  coerceValue(val) {
    if (val === null || val === undefined) return null;

    if (typeof val === 'boolean') return val;
    if (typeof val === 'number') return val;

    if (val instanceof Date) {
      return isNaN(val.getTime()) ? null : val.toISOString();
    }

    if (Array.isArray(val)) {
      return val
        .map(item => this.coerceValue(item))
        .filter(item => item !== null);
    }

    if (typeof val === 'object') {
      try { return JSON.stringify(val); } catch (e) { return String(val); }
    }

    if (typeof val === 'string') {
      // Detect ISO-like date strings: YYYY-MM-DD or YYYY-MM-DDT...
      // Deliberately excludes plain numbers ("149.90") and arbitrary strings.
      if (/^\d{4}-\d{2}-\d{2}(T[\d:.Z+\-]*)?$/.test(val)) {
        const d = new Date(val);
        if (!isNaN(d.getTime())) return d.toISOString();
      }
      return val;
    }

    return val;
  }

  /**
   * Resolve the dtype label used by the schema for a coerced value.
   * @param {*} v
   * @returns {'array'|'number'|'string'|'boolean'|'object'|'undefined'}
   */
  _dtypeOf(v) {
    if (Array.isArray(v)) return 'array';
    return typeof v; // 'number' | 'string' | 'boolean'
  }

  /**
   * Coerce a value to the declared target type.
   * Returns null when the value cannot be coerced (caller falls back to String).
   * @param {*} v
   * @param {string} target  'number' | 'string' | 'boolean' | 'array'
   * @returns {*}
   */
  _coerceToType(v, target) {
    switch (target) {
      case 'string':  return String(v);
      case 'number': { const n = Number(v); return Number.isFinite(n) ? n : null; }
      case 'boolean':
        if (v === true  || v === 'true'  || v === 1 || v === '1') return true;
        if (v === false || v === 'false' || v === 0 || v === '0') return false;
        return null;
      case 'array':   return Array.isArray(v) ? v : null;
      default:        return v; // bilinmeyen target → dokunma
    }
  }

  /**
   * Enforce the declared dtype for a property (called AFTER coerceValue()).
   * - schema'da yoksa / zaten doğru tipte → değer aynen.
   * - uyuşmuyorsa → declared tipe coerce; coerce edilemiyorsa String'e düşür.
   * Property asla atılmaz, console.warn yapılmaz.
   * @param {string} key
   * @param {*} coerced  coerceValue() çıktısı
   * @param {Object} schema  config.propertyTypes
   * @returns {*}
   */
  enforceType(key, coerced, schema) {
    if (coerced === null || coerced === undefined) return coerced;
    const target = schema && schema[key];
    if (!target) return coerced;                           // beyan edilmemiş
    if (this._dtypeOf(coerced) === target) return coerced; // zaten doğru
    const fixed = this._coerceToType(coerced, target);
    return fixed === null ? String(coerced) : fixed;       // çevrilemezse string fallback
  }

  /**
   * Format a parameter to ensure it's in the correct format for the API
   * @param {string} key - Parameter key
   * @param {any} value - Parameter value
   * @returns {Object|null} - Formatted parameter object, or null if value should be skipped
   */
  formatParam(key, value) {
    const coerced = this.coerceValue(value);
    if (coerced === null) return null;
    return { key: String(key), value: coerced };
  }
  
  /**
   * Generate event timestamp in milliseconds
   * @returns {number} - Current timestamp
   */
  getTimestamp() {
    return Date.now();
  }
  
  /**
   * Format date as ISO string
   * @param {Date|number} date - Date object or timestamp
   * @returns {string} - ISO date string
   */
  formatDate(date) {
    if (typeof date === 'number') {
      date = new Date(date);
    }
    return date instanceof Date ? date.toISOString() : new Date().toISOString();
  }
  
  /**
   * Validate event data
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event data
   * @returns {boolean} - Whether the event data is valid
   */
  validateEvent(eventName, eventData) {
    if (!eventName || typeof eventName !== 'string') {
      return false;
    }
    
    if (eventData && typeof eventData !== 'object') {
      return false;
    }
    
    return true;
  }
  
  /**
   * Create a platform-agnostic event object
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event data
   * @param {string} platform - Event platform (default: 'custom')
   * @returns {Object} - Normalized event object
   */
  createEvent(eventName, eventData, platform = 'custom') {
    return {
      platform,
      event: eventName,
      data: eventData || {},
      _timestamp: this.formatDate(new Date())
    };
  }
} 